<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Update Start Date/Time"
    :button-label="props.buttonLabel"
    has-changes
    @save="save"
    @dismiss="dismiss"
  >
    <vs-wrapper>
      <vs-text>I want to</vs-text>

      <v-radio-group v-model="data.scheduleOption" hide-details class="ma-0 pa-0">
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="delay" data-test="bulk-update-delay" />
          </v-col>
          <v-col>
            <vs-text>Delay</vs-text>
          </v-col>
        </v-row>
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="expedite" data-test="bulk-update-expedite" />
          </v-col>
          <v-col>
            <vs-text>Move to earlier date/time</vs-text>
          </v-col>
        </v-row>
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="overwrite" data-test="bulk-update-overwrite" />
          </v-col>
          <v-col>
            <vs-text>Overwrite</vs-text>
          </v-col>
        </v-row>
      </v-radio-group>

      <overwrite-schedule-form
        v-if="data.scheduleOption === 'overwrite'"
        :is-saving="saving"
        :start-time="data.startTime"
        :end-time="data.endTime"
        @update-start-time="updateStartTime"
        @update-end-time="updateEndTime"
      />
      <reschedule-form
        v-else
        :saving="saving"
        :time-delta-quantity="data.timeDeltaQuantity"
        :time-delta-unit="data.timeDeltaUnit"
        :schedule-option="data.scheduleOption"
        @update-quantity="updateTimeDeltaQuantity"
        @update-unit="updateTimeDeltaUnit"
      />
    </vs-wrapper>

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to update schedule
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import moment from 'moment'
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import OverwriteScheduleForm from '../project/OverwriteScheduleForm.vue'
import RescheduleForm from '../project/RescheduleForm.vue'
import { ref, reactive, onMounted } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { bulkOverwriteSchedule, bulkReschedule } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)

interface Data {
  scheduleOption: 'delay' | 'expedite' | 'overwrite'
  timeDeltaUnit: 'day' | 'hour'
  timeDeltaQuantity: number
  startTime: moment.Moment | null
  endTime: moment.Moment | null
}

const data: Data = reactive({
  scheduleOption: 'delay',
  timeDeltaUnit: 'day',
  timeDeltaQuantity: 1,
  startTime: null,
  endTime: null,
})

onMounted(() => {
  data.startTime = defaultStartTime()
  data.endTime = defaultEndTime()
})

// Reschedule all job start and end times by the same time delta
const reschedule = async () => {
  saving.value = true
  try {
    await bulkReschedule(
      props.selectedJobs.map((job) => job.number),
      timeDeltaInSeconds(),
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, {
        requested_start: moment(job.requested_start)
          .add(timeDeltaInSeconds(), 'seconds')
          .toISOString(),
        requested_end: moment(job.requested_end).add(timeDeltaInSeconds(), 'seconds').toISOString(),
      }),
    )
    emits('updated', { message: 'Schedule updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}
const timeDeltaInSeconds = (): number => {
  let amount = data.timeDeltaQuantity

  if (data.timeDeltaUnit === 'day') {
    amount = amount * 24
  }

  if (data.scheduleOption === 'expedite') {
    amount = amount * -1
  }

  return amount * 3600
}
const updateTimeDeltaQuantity = (value: string) => (data.timeDeltaQuantity = parseInt(value, 10))
const updateTimeDeltaUnit = (value: 'day' | 'hour') => (data.timeDeltaUnit = value)

// Overwrite all jobs to have the same start and end time
const overwriteSchedule = async () => {
  saving.value = true
  try {
    await bulkOverwriteSchedule(
      props.selectedJobs.map((job) => job.number),
      data.startTime,
      data.endTime,
    )
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, {
        requested_start: data.startTime?.toISOString(),
        requested_end: data.endTime?.toISOString(),
      }),
    )
    emits('updated', { message: 'Schedule updated', updatedJobs })
  } catch (err) {
    error.value = true
  } finally {
    saving.value = false
  }
}

const defaultStartTime = (): moment.Moment | null => {
  const startTimes = new Set(props.selectedJobs.map((job) => job.requested_start))
  return startTimes.size === 1 ? moment([...startTimes][0]) : null
}

const defaultEndTime = (): moment.Moment | null => {
  const endTimes = new Set(props.selectedJobs.map((job) => job.requested_end))
  return endTimes.size === 1 ? moment([...endTimes][0]) : null
}
const updateStartTime = (value: moment.Moment) => (data.startTime = value)
const updateEndTime = (value: moment.Moment) => (data.endTime = value)

const save = () => {
  if (data.scheduleOption === 'overwrite') {
    overwriteSchedule()
  } else {
    reschedule()
  }
}

const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')
</script>
